import { useEffect, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import {
    ensureObsidianDashboardFonts,
    OBSIDIAN_BODY_FONT,
    OBSIDIAN_DISPLAY_FONT,
} from '../../../constants/obsidianDashboardTheme';
import { CampaignArt, ConnectArt, DeliverArt, PaidArt, RateArt } from './BuzzBridgeOnboardingArt';
import { BuzzBridgeOnboardingMobile } from './BuzzBridgeOnboardingMobile';

const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';

const STEPS: {
    n: string;
    tag: string;
    tagColor: string;
    title: string;
    body: string;
    art: ReactNode;
    accent?: boolean;
    done?: boolean;
}[] = [
    {
        n: '01',
        tag: 'Required',
        tagColor: MINT,
        title: 'Connect',
        body: 'Link Instagram, TikTok, or YouTube so brands can verify your profile and real reach.',
        art: <ConnectArt />,
        accent: true,
    },
    {
        n: '02',
        tag: 'Presets',
        tagColor: '#767575',
        title: 'Update your profile',
        body: 'Update your profile with your creator details, and price your work.',
        art: <RateArt />,
    },
    {
        n: '03',
        tag: 'Direct',
        tagColor: '#767575',
        title: 'Get a campaign',
        body: 'Get invited directly by partner brands or apply to open creator briefs.',
        art: <CampaignArt />,
    },
    {
        n: '04',
        tag: 'Studio',
        tagColor: '#767575',
        title: 'Deliver',
        body: 'Shoot authentic UGC and submit deliverables in the dedicated workspace.',
        art: <DeliverArt />,
    },
    {
        n: '05',
        tag: 'Instant',
        tagColor: MINT,
        title: 'Get paid',
        body: 'Flat payout clears into your balance for instant cash-out, plus earn bonus commission.',
        art: <PaidArt />,
        done: true,
    },
];

export interface BuzzBridgeCreatorOnboardingScreenProps {
    onConnectClick: () => void;
}

export function BuzzBridgeCreatorOnboardingScreen({ onConnectClick }: BuzzBridgeCreatorOnboardingScreenProps) {
    useEffect(() => {
        ensureObsidianDashboardFonts();
    }, []);

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: { xs: 2, md: 4 },
                px: { xs: 0.5, md: 1 },
            }}>
            <Box sx={{ display: { xs: 'block', lg: 'none' }, width: '100%' }}>
                <BuzzBridgeOnboardingMobile onConnectClick={onConnectClick} />
            </Box>
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 1024,
                    display: { xs: 'none', lg: 'flex' },
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.75,
                        py: 0.75,
                        mb: 3,
                        borderRadius: 999,
                        bgcolor: '#201f1f',
                    }}>
                    <Box
                        sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: MINT, boxShadow: '0 0 8px #00fdc1' }}
                    />
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 12,
                            fontWeight: 600,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: MINT,
                        }}>
                        BuzzBridge Creator Setup
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#484847' }}>/</Typography>
                    <Typography sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 12, color: ON_VARIANT }}>
                        Stage 0 of 5
                    </Typography>
                </Box>

                <Box sx={{ textAlign: 'center', maxWidth: 640, mb: { xs: 5, md: 7 } }}>
                    <Typography
                        component="h1"
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 800,
                            fontSize: { xs: 32, md: 48 },
                            letterSpacing: '-0.03em',
                            lineHeight: 1.15,
                            color: '#fff',
                        }}>
                        Get hired. Make the content.{' '}
                        <Box
                            component="span"
                            sx={{
                                background: `linear-gradient(90deg, ${GOLD}, #ecaa00, ${GOLD})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}>
                            Get paid.
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            mt: 2,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: { xs: 16, md: 18 },
                            color: ON_VARIANT,
                            lineHeight: 1.6,
                            maxWidth: 560,
                            mx: 'auto',
                        }}>
                        Thspian connects you with brands that pay creators for authentic UGC. Connect a social account
                        to launch your public profile.
                    </Typography>
                </Box>

                <Box sx={{ width: '100%', position: 'relative', mb: 5 }}>
                    <Box
                        sx={{
                            display: { xs: 'none', lg: 'block' },
                            position: 'absolute',
                            top: 88,
                            left: '8%',
                            right: '8%',
                            height: 2,
                            background:
                                'linear-gradient(90deg, rgba(255,201,101,0.3), rgba(236,170,0,0.2), rgba(0,253,193,0.3))',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(5, 1fr)' },
                            gap: 2,
                            position: 'relative',
                            zIndex: 1,
                        }}>
                        {STEPS.map(step => (
                            <Box
                                key={step.n}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    bgcolor: '#131313',
                                    p: 2.5,
                                    borderRadius: 1.5,
                                    boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
                                    '&:hover': { bgcolor: '#1a1919' },
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 2,
                                    }}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                                            fontWeight: 700,
                                            fontSize: 12,
                                            color: step.done ? MINT : step.accent ? GOLD : '#fff',
                                            bgcolor: step.done
                                                ? '#006c50'
                                                : step.accent
                                                ? 'rgba(254,183,0,0.2)'
                                                : '#262626',
                                            boxShadow:
                                                step.accent || step.done
                                                    ? `0 0 0 1px ${
                                                          step.done ? 'rgba(0,253,193,0.3)' : 'rgba(255,201,101,0.3)'
                                                      }`
                                                    : 'none',
                                        }}>
                                        {step.n}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                            color: step.tagColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}>
                                        {step.accent ? <VerifiedOutlinedIcon sx={{ fontSize: 14 }} /> : null}
                                        {step.tag}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        height: 128,
                                        borderRadius: 1.5,
                                        bgcolor: 'rgba(32,31,31,0.6)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2.5,
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            bgcolor:
                                                step.accent || step.done
                                                    ? 'rgba(0,253,193,0.1)'
                                                    : 'rgba(255,201,101,0.1)',
                                            filter: 'blur(16px)',
                                        }}
                                    />
                                    <Box sx={{ position: 'relative' }}>{step.art}</Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 0.75,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                                            fontWeight: 700,
                                            fontSize: 16,
                                            color: '#fff',
                                        }}>
                                        {step.title}
                                    </Typography>
                                    {step.done ? (
                                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: MINT }} />
                                    ) : (
                                        <ArrowForwardIcon sx={{ fontSize: 14, color: ON_VARIANT }} />
                                    )}
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontSize: 12,
                                        color: ON_VARIANT,
                                        lineHeight: 1.5,
                                    }}>
                                    {step.body}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 672,
                        px: 2.5,
                        py: 1.75,
                        mb: 6,
                        borderRadius: 999,
                        bgcolor: '#1a1919',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.75,
                    }}>
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,253,193,0.15)',
                            color: MINT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                        <StarsOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: { xs: 12, md: 14 },
                            color: ON_VARIANT,
                            lineHeight: 1.5,
                        }}>
                        <Box component="strong" sx={{ color: '#fff', fontWeight: 600 }}>
                            Earn two ways:
                        </Box>{' '}
                        Guaranteed campaign base pay on delivery, plus secondary sales or install commission after
                        campaigns close.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.16em',
                            textTransform: 'uppercase',
                            color: GOLD,
                            mb: 1.5,
                        }}>
                        Ready to Start?
                    </Typography>
                    <Button
                        onClick={onConnectClick}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            px: 5,
                            py: 2,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: { xs: 14, md: 16 },
                            color: '#392700',
                            bgcolor: GOLD,
                            boxShadow: '0 4px 24px rgba(255,201,101,0.25)',
                            '&:hover': { bgcolor: '#ecaa00' },
                            '&:active': { transform: 'scale(0.98)' },
                        }}>
                        Connect your socials
                    </Button>
                    <Typography sx={{ mt: 1.75, fontFamily: OBSIDIAN_BODY_FONT, fontSize: 12, color: ON_VARIANT }}>
                        Instagram, TikTok, or YouTube. You can add the others later.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
